import { useMemo, useRef, useState } from "react";
import {
  Loader2, Upload, Undo2, FileSpreadsheet, CheckCircle2, AlertTriangle, Pencil, X,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ItemPickerDialog from "./ItemPickerDialog";
import { FrsImportSummary, FrsQuotesTable } from "./FrsImportSummary";
import { estApi } from "../lib/api";

const ACTIONS = ["accept", "review", "skip"];
const VIA_LABEL = {
  alias: "learned alias",
  item_number: "part number",
  item_number_bare: "part number (prefix stripped)",
  bobrick_bradley_xref: "Bobrick→Bradley x-ref",
  description: "exact description",
  fuzzy_description: "similar description",
  fuzzy_part: "similar part number",
  manual: "pulled by you",
  none: "—",
};

const conf = (v) => `${Math.round((v || 0) * 100)}%`;

/**
 * "Upload Takeoff" for the Estimating module. Reads an ABS FRS workbook natively:
 * INFO tab for project info, "<SCOPE> IN" tabs for quantities, the matrix tabs for the
 * room breakdown. No AI — every row shows how it matched and at what confidence, and
 * nothing is written until you commit.
 *
 * estimateId = null means "create a new estimate from this FRS" (Proposals page entry).
 */
export default function FrsUploadDialog({ open, onOpenChange, estimateId = null,
                                          onCommitted, onCreated }) {
  const [preview, setPreview] = useState(null);
  const [targetId, setTargetId] = useState(estimateId);
  const [busy, setBusy] = useState(false);
  const [mergeMode, setMergeMode] = useState(true);
  const [picker, setPicker] = useState(null);      // { index, search }
  const inputRef = useRef(null);

  const close = (v, committed = false) => {
    onOpenChange(v);
    if (!v) {
      if (committed) { setPreview(null); setTargetId(estimateId); return; }
      // A Proposals-page upload creates the estimate up front; drop it again if the
      // user backs out so cancelling never leaves an empty record behind.
      if (!estimateId && targetId) estApi.deleteEstimate(targetId).catch(() => {});
      setPreview(null);
      setTargetId(estimateId);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      if (estimateId) {
        setTargetId(estimateId);
        try {
          setPreview(await estApi.frsPreview(estimateId, file));
        } catch (err) {
          // Not an FRS workbook — fall back to the header-mapped takeoff parser so one button
          // handles both an FRS and a plain Bluebeam/takeoff spreadsheet.
          const detail = err?.response?.data?.detail || "";
          if (!String(detail).includes("FRS workbook") && !String(detail).includes(".xlsx")) throw err;
          const form = new FormData();
          form.append("file", file);
          form.append("mode", "takeoff");
          const res = await estApi.takeoffPreview(estimateId, form);
          setPreview({ ...res, mode: "takeoff", scopes: [], project_info: {},
                       file_name: file.name });
          toast.info("Not an FRS workbook — read with column mapping instead");
        }
      } else {
        if (targetId) await estApi.deleteEstimate(targetId).catch(() => {});
        const res = await estApi.frsCreateEstimate(file);
        setPreview(res.preview);
        if (!res.estimate) {
          setTargetId(null);
          toast.warning(res.preview.message || "Nothing to import");
        } else {
          setTargetId(res.estimate.id);
          toast.success(`Created ${res.estimate.estimate_number} — ${res.estimate.project_name}`);
        }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" && detail
        ? detail : "Could not read that file");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const setRow = (idx, patch) => setPreview((p) => ({
    ...p, rows: p.rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
  }));

  const applyPick = (item) => {
    setRow(picker.index, {
      match: {
        kind: "item", id: item.id, name: item.name, item_number: item.item_number,
        confidence: 1, via: "manual",
      },
      action: "accept",
    });
    setPicker(null);
  };

  const counts = useMemo(() => {
    const rows = preview?.rows || [];
    return {
      accept: rows.filter((r) => r.action === "accept").length,
      review: rows.filter((r) => r.action === "review").length,
      skip: rows.filter((r) => r.action === "skip").length,
    };
  }, [preview]);

  const mc = preview?.merge_counts || {};
  const quoteCount = (preview?.quotes?.rows || []).length;
  const isRevision = (mc.qty_update || 0) + (mc.same || 0) + (mc.dropped || 0) > 0;

  const commit = async () => {
    setBusy(true);
    try {
      const res = await estApi.takeoffCommit(targetId, {
        staging_id: preview.staging_id, rows: preview.rows, merge: mergeMode,
      });
      toast.success(
        [`${res.added} line(s) added`,
         res.qty_updated ? `${res.qty_updated} qty updated` : "",
         res.unchanged ? `${res.unchanged} unchanged` : "",
         res.dropped_flagged ? `${res.dropped_flagged} no longer in the file` : "",
         res.aliases_learned ? `${res.aliases_learned} alias(es) learned` : "",
         res.quotes_created ? `${res.quotes_created} vendor quote(s) added` : "",
         res.quotes_updated ? `${res.quotes_updated} vendor quote(s) updated` : "",
        ].filter(Boolean).join(" · "),
        {
          duration: 20000,
          action: {
            label: "Undo",
            onClick: async () => {
              const undone = await estApi.undoTakeoffBatch(targetId, res.batch_id);
              onCommitted?.(undone);
              toast.success(`${undone.removed} line(s) removed`);
            },
          },
        });
      if (estimateId) onCommitted?.(res);
      else onCreated?.(targetId);
      close(false, true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally {
      setBusy(false);
    }
  };

  const info = preview?.project_info || {};
  const infoChips = [
    ["Project", info.project_name],
    ["Project #", info.frs_project_number],
    ["Architect", info.architect],
    ["Location", [info.city, info.state].filter(Boolean).join(", ")],
    ["Estimator", info.estimator_name],
    ["Drawing set", info.drawing_set],
  ].filter(([, v]) => v);

  return (
    <>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="max-w-6xl" data-testid="frs-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload takeoff</DialogTitle>
            <DialogDescription className="text-xs">
              An ABS FRS workbook is read natively — the INFO tab for project details, each
              "&lt;SCOPE&gt; IN" tab for quantities, the matrix tabs for the room breakdown, and
              blank scope tabs skipped. Any other spreadsheet falls back to column mapping.
            </DialogDescription>
          </DialogHeader>

          {!preview ? (
            <div className="space-y-3">
              <label className="flex h-40 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-xs text-slate-500 transition-colors hover:border-[#007BFF] hover:bg-slate-50"
                     data-testid="frs-dropzone">
                {busy ? (
                  <>
                    <Loader2 className="mb-2 h-6 w-6 animate-spin text-[#007BFF]" />
                    <span>Reading the workbook and matching to your cost library…</span>
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="mb-2 h-7 w-7 text-slate-400" />
                    <span className="text-sm font-medium text-slate-700">
                      Drop your FRS here, or click to choose
                    </span>
                    <span className="mt-1 text-[11px] text-slate-400">
                      .xlsx FRS, or any takeoff spreadsheet (.xlsx / .csv)
                    </span>
                  </>
                )}
                <input ref={inputRef} type="file" accept=".xlsx,.xlsm,.xls,.csv" className="hidden"
                       disabled={busy} onChange={(e) => upload(e.target.files?.[0])}
                       data-testid="frs-file-input" />
              </label>
              <p className="text-[10px] leading-relaxed text-slate-400">
                Bobrick parts written as <span className="font-mono">B-3944</span> are matched to
                the catalog's bare <span className="font-mono">3944</span>, and a Bobrick model
                with no catalog entry falls back to its Bradley cross-reference. Anything that
                can't be resolved confidently is flagged for you to pick by hand — never priced
                on a guess.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {infoChips.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2"
                     data-testid="frs-project-info">
                  {infoChips.map(([k, v]) => (
                    <span key={k} className="text-[11px] text-slate-500">
                      {k}: <strong className="font-medium text-slate-800">{v}</strong>
                    </span>
                  ))}
                </div>
              )}

              {preview.mode === "frs" && <FrsImportSummary preview={preview} />}

              {preview.mode === "frs" && (preview.quotes?.rows || []).length > 0 && (
                <FrsQuotesTable quotes={preview.quotes} />
              )}

              {preview.rows.length > 0 && (
              <div className="flex items-center gap-3 text-xs text-slate-600" data-testid="frs-counts">
                <span>{preview.counts.total} products</span>
                <span className="flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {counts.accept} ready
                </span>
                <span className="flex items-center gap-1 text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5" /> {counts.review} need review
                </span>
                {counts.skip > 0 && <span className="text-slate-400">{counts.skip} skipped</span>}
                {preview.mode === "frs" && (
                  <span className="ml-auto text-slate-400">
                    {preview.counts.scopes_imported} scope(s) in · {preview.counts.scopes_blank} blank
                  </span>
                )}
              </div>
              )}

              {isRevision && (
                <div className="rounded-lg border border-[#007BFF]/30 bg-[#007BFF]/5 px-3 py-2"
                     data-testid="frs-merge-strip">
                  <p className="text-xs font-semibold text-slate-800">
                    This looks like a revision of what is already on the estimate
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    {mc.qty_update || 0} quantity change(s) · {mc.add || 0} new part(s) ·{" "}
                    {mc.same || 0} unchanged · {mc.dropped || 0} line(s) no longer in the file
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-4">
                    <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
                      <input type="radio" checked={mergeMode} onChange={() => setMergeMode(true)}
                             data-testid="frs-mode-merge" />
                      <span><b>Merge</b> — move quantities, keep your pricing, add what's new</span>
                    </label>
                    <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
                      <input type="radio" checked={!mergeMode} onChange={() => setMergeMode(false)}
                             data-testid="frs-mode-append" />
                      <span><b>Append</b> — add every row as a new line</span>
                    </label>
                  </div>
                </div>
              )}

              {preview.rows.length > 0 && (
              <div className="max-h-[22rem] overflow-auto rounded border border-slate-200">
                <table className="w-full text-xs" data-testid="frs-review-table">
                  <thead className="sticky top-0 z-10 bg-slate-50">
                    <tr className="text-slate-500">
                      <th className="px-2 py-1.5 text-left font-medium">Scope</th>
                      <th className="px-2 py-1.5 text-left font-medium">FRS part</th>
                      <th className="px-2 py-1.5 text-left font-medium">Description</th>
                      <th className="px-2 py-1.5 text-right font-medium">Qty</th>
                      {isRevision && (
                        <th className="px-2 py-1.5 text-left font-medium">Change</th>
                      )}
                      <th className="px-2 py-1.5 text-left font-medium">Matched to</th>
                      <th className="px-2 py-1.5 text-right font-medium">Labor</th>
                      <th className="px-2 py-1.5 text-right font-medium">Conf.</th>
                      <th className="px-2 py-1.5 text-left font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={r.row}
                          className={`border-t border-slate-100 ${
                            r.action === "skip" ? "opacity-40" : ""}`}
                          data-testid={`frs-row-${r.row}`}>
                        <td className="px-2 py-1.5">
                          <div className="font-medium text-slate-700">{r.scope}</div>
                          <div className="text-[10px] text-slate-400">{r.section}</div>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[11px] text-slate-500">
                          {r.item_number || "—"}
                          {r.keynote && (
                            <div className="font-sans text-[10px] text-slate-400">{r.keynote}</div>
                          )}
                        </td>
                        <td className="max-w-[16rem] px-2 py-1.5">
                          <div className="truncate text-slate-800" title={r.label}>{r.label}</div>
                          {r.notes && (
                            <div className="truncate text-[10px] text-slate-400" title={r.notes}>
                              {r.notes}
                            </div>
                          )}
                          {(r.flags || []).map((f) => (
                            <div key={f} className="truncate text-[10px] font-medium text-amber-700" title={f}
                                 data-testid={`frs-row-flag-${r.row}`}>{f}</div>
                          ))}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">
                          {r.qty}
                        </td>
                        {isRevision && (
                          <td className="whitespace-nowrap px-2 py-1.5"
                              data-testid={`frs-change-${r.row}`}>
                            {!mergeMode || r.merge?.action === "add" ? (
                              <span className="text-[11px] text-blue-700">new line</span>
                            ) : r.merge?.action === "qty_update" ? (
                              <span className="text-[11px] font-medium text-emerald-700 tabular-nums">
                                {r.merge.old_qty} &rarr; {r.merge.new_qty}
                              </span>
                            ) : (
                              <span className="text-[11px] text-slate-400">unchanged</span>
                            )}
                          </td>
                        )}
                        <td className="max-w-[15rem] px-2 py-1.5">
                          {r.match.kind === "unmatched" ? (
                            <span className="text-amber-700">
                              no match — free-text line to price{r.labor?.hours_per_unit > 0 ? ", labor carried" : ""}
                            </span>
                          ) : (
                            <>
                              <div className="truncate text-slate-700" title={r.match.name}>
                                {r.match.name}
                              </div>
                              <div className="text-[10px] text-slate-400">
                                {r.match.item_number ? `${r.match.item_number} · ` : ""}
                                {VIA_LABEL[r.match.via] || r.match.via}
                              </div>
                            </>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums" data-testid={`frs-row-labor-${r.row}`}
                            title={r.labor?.labor_item ? `${r.labor.labor_item} — ${r.labor.via === "part" ? "cost-library part" : r.labor.via === "keyword" ? "keyword" : "scope default"}` : (r.labor?.flag || "")}>
                          {r.labor?.hours_per_unit > 0 ? (
                            <span className={r.labor.via === "scope_default" ? "text-amber-700" : "text-slate-700"}>{r.labor.hours_per_unit} h</span>
                          ) : <span className="font-medium text-red-600">0 h</span>}
                          {r.labor?.via && r.labor.via !== "part" && r.labor.hours_per_unit > 0 && (
                            <div className="truncate text-[10px] text-slate-400">{r.labor.labor_item}</div>)}
                        </td>
                        <td className={`px-2 py-1.5 text-right tabular-nums ${
                          r.match.confidence >= 0.8 ? "text-emerald-700" : "text-amber-700"}`}>
                          {conf(r.match.confidence)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5">
                          <select value={r.action}
                                  onChange={(e) => setRow(i, { action: e.target.value })}
                                  className="h-6 rounded border border-slate-200 bg-white px-1 text-[11px]"
                                  data-testid={`frs-action-${r.row}`}>
                            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                          </select>
                          <button onClick={() => setPicker({ index: i, search: r.item_number || r.label })}
                                  className="ml-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-[#007BFF]"
                                  title="Pick the cost library item by hand"
                                  data-testid={`frs-pick-${r.row}`}>
                            <Pencil className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
              {mergeMode && (preview.dropped || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2"
                     data-testid="frs-dropped">
                  <p className="text-[11px] font-semibold text-amber-800">
                    {preview.dropped.length} line(s) on this estimate are not in the new file —
                    nothing is deleted, review them yourself
                  </p>
                  <ul className="mt-1 max-h-24 space-y-0.5 overflow-auto">
                    {preview.dropped.map((d) => (
                      <li key={d.line_id} className="truncate text-[11px] text-amber-700"
                          data-testid={`frs-dropped-${d.line_id}`}>
                        {d.section ? `${d.section} · ` : ""}
                        {d.item_number ? `${d.item_number} · ` : ""}{d.description} (qty {d.qty})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-[10px] leading-snug text-slate-400">
                Rows left on "review" are added as free-text lines with no cost and flagged for
                pricing, so nothing gets silently mispriced. Everything lands in one batch you can
                undo in a click.
              </p>
            </div>
          )}

          <DialogFooter>
            {preview && (
              <Button variant="ghost" className="text-xs" onClick={() => setPreview(null)}
                      data-testid="frs-back">
                <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Choose another file
              </Button>
            )}
            <Button variant="outline" onClick={() => close(false)} data-testid="frs-cancel">
              <X className="mr-1.5 h-3.5 w-3.5" /> Cancel
            </Button>
            <Button onClick={commit} data-testid="frs-commit"
                    disabled={!preview || busy || preview.empty || !targetId
                      || (isRevision && mergeMode && quoteCount === 0
                          && (mc.qty_update || 0) + (mc.add || 0) === 0)}>
              {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    : <Upload className="mr-1.5 h-3.5 w-3.5" />}
              {isRevision && mergeMode
                ? `Apply ${mc.qty_update || 0} qty change(s) and ${mc.add || 0} new line(s)`
                : `Add ${counts.accept + counts.review} line(s) to the estimate`}
              {quoteCount > 0 && ` · ${quoteCount} quote(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ItemPickerDialog
        open={!!picker} onOpenChange={(v) => !v && setPicker(null)}
        initialSearch={picker?.search || ""} onPick={applyPick}
      />
    </>
  );
}
